import { load } from "../../events/load";

/**
 * Contact form behavior for contacts and cta blocks.
 * Includes mask, validation, loading state and thank-you view toggling.
 */
class ContactFormBlock {
    constructor(container, successClass, wrapperDisplayType) {
        this.container = container;
        this.formWrapper = container.querySelector("#contact-form");
        this.form = container.querySelector(".reintegration-form form");
        this.successClass = successClass;
        this.wrapperDisplayType = wrapperDisplayType;
        this.submitBtn = this.form?.querySelector('button[type="submit"]');
        this.submitBtnText = this.submitBtn?.querySelector(".btn-secondary__text, .btn__text");
        this.originalBtnText = this.submitBtnText?.textContent?.trim() || "Надіслати";
        this.loadingTimeout = null;
        this.resetSuccessTimeout = null;
        this.successView = null;

        if (!this.formWrapper || !this.form) {
            return;
        }

        this.init();
    }

    init() {
        this.ensureRequiredFields();
        this.createSuccessView();
        this.initValidation();
        this.initSuccessHandler();
    }

    ensureRequiredFields() {
        const surnameInput = this.form.querySelector('#contact-surname, input[name="Прізвище"]');
        if (surnameInput) {
            surnameInput.setAttribute("required", "required");
            surnameInput.setAttribute("aria-required", "true");
        }
    }

    createSuccessView() {
        this.successView = document.createElement("div");
        this.successView.className = this.successClass;
        this.successView.style.display = "none";
        this.successView.innerHTML = `
            <div class="${this.successClass}__content">
                <h3 class="${this.successClass}__title">ДЯКУЄМО! ВАШУ ЗАЯВКУ <br> УСПІШНО НАДІСЛАНО</h3>
                <span class="${this.successClass}__text">Ми з вами зв'яжемось!</span>
            </div>
        `;

        this.container.appendChild(this.successView);
    }

    initValidation() {
        const inputs = this.form.querySelectorAll(".contact-form__input");
        inputs.forEach((input) => {
            input.addEventListener("input", () => {
                input.classList.remove("is-invalid");
            });
        });
    }

    initSuccessHandler() {
        document.addEventListener("reintegrationFormSubmitted", (event) => {
            const submittedForm = event?.detail?.form;
            const response = event?.detail?.data;

            if (!submittedForm || !this.container.contains(submittedForm)) {
                return;
            }

            clearTimeout(this.loadingTimeout);
            this.setLoadingState(false);

            if (!response?.success && !response?.data?.success) {
                const data = response?.data || response;
                const errorMessage = data?.message || "Помилка при відправці форми";

                if (data?.field) {
                    this.showFieldError(data.field, errorMessage);
                } else {
                    alert(errorMessage);
                }
                return;
            }

            this.resetForm();
            this.showSuccessView();

            clearTimeout(this.resetSuccessTimeout);
            this.resetSuccessTimeout = setTimeout(() => {
                this.hideSuccessView();
            }, 5000);
        });
    }

    showFieldError(fieldName, message) {
        // Try to find by name, then by id
        const input = this.form.querySelector(`[name="${fieldName}"]`) ||
            this.form.querySelector(`#${fieldName}`) ||
            this.form.querySelector(`[id*="${fieldName}"]`);

        if (!input) {
            alert(message);
            return;
        }

        input.classList.add("is-invalid");

        let errorElement = input.parentNode.querySelector(".contact-form__error");
        if (!errorElement) {
            errorElement = document.createElement("span");
            errorElement.className = "contact-form__error";
            input.parentNode.appendChild(errorElement);
        }

        errorElement.textContent = message;
        errorElement.classList.add("is-visible");

        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        input.focus();
    }

    clearErrors() {
        this.form.querySelectorAll(".is-invalid").forEach(el => el.classList.remove("is-invalid"));
        this.form.querySelectorAll(".contact-form__error").forEach(el => el.classList.remove("is-visible"));
    }

    validateForm() {
        this.clearErrors();
        const requiredInputs = this.form.querySelectorAll(".contact-form__input[required]");
        let isValid = true;
        let firstInvalidInput = null;

        const i18n = window.params?.i18n || {};

        requiredInputs.forEach((input) => {
            const value = input.value.trim();
            const fieldName = (input.name || input.id || "").toLowerCase();

            // 1. Length Check
            if (value.length > 500) {
                isValid = false;
                this.showFieldError(input.name || input.id, i18n.form_error_too_long || "Too many characters");
                if (!firstInvalidInput) firstInvalidInput = input;
                return;
            }

            // 2. Phone Check
            if (input.type === "tel" || input.id === "contact-phone" || fieldName.includes("phone") || fieldName.includes("телефон")) {
                const digits = value.replace(/\D/g, "");
                if (digits.length !== 12) {
                    isValid = false;
                    this.showFieldError(input.name || input.id, i18n.phone_error || "Invalid phone number");
                    if (!firstInvalidInput) firstInvalidInput = input;
                }
                return;
            }

            // 3. Name/Surname Check (No digits, No symbols)
            const isNameField = fieldName.includes("імʼя") || fieldName.includes("імя") || fieldName.includes("name") || fieldName.includes("surname") || fieldName.includes("прізвище");

            if (isNameField && value) {
                if (value.length > 50) {
                    isValid = false;
                    this.showFieldError(input.name || input.id, i18n.form_error_name_too_long || "Name too long");
                    if (!firstInvalidInput) firstInvalidInput = input;
                    return;
                }

                // Strictly no digits and minimal punctuation for names
                const nameRegex = /^[\p{L}\s\-\.]+$/u;
                if (!nameRegex.test(value)) {
                    isValid = false;
                    this.showFieldError(input.name || input.id, i18n.form_error_invalid_chars || "Invalid characters");
                    if (!firstInvalidInput) firstInvalidInput = input;
                    return;
                }
            }

            // 4. Global Emoji Check
            const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
            if (emojiRegex.test(value)) {
                isValid = false;
                this.showFieldError(input.name || input.id, i18n.form_error_contains_emojis || "Emojis not allowed");
                if (!firstInvalidInput) firstInvalidInput = input;
                return;
            }

            // 5. Empty Check
            if (!value) {
                isValid = false;
                input.classList.add("is-invalid");
                if (!firstInvalidInput) firstInvalidInput = input;
            }
        });

        if (!isValid && firstInvalidInput) {
            firstInvalidInput.focus();
        }

        return isValid;
    }

    setLoadingState(isLoading) {
        if (!this.submitBtn || !this.submitBtnText) {
            return;
        }

        if (isLoading) {
            this.submitBtnText.textContent = "Надсилання...";
            this.submitBtn.disabled = true;
            this.submitBtn.style.opacity = "0.7";
            this.submitBtn.style.pointerEvents = "none";
            return;
        }

        this.submitBtnText.textContent = this.originalBtnText;
        this.submitBtn.disabled = false;
        this.submitBtn.style.opacity = "";
        this.submitBtn.style.pointerEvents = "";
    }

    resetForm() {
        const inputs = this.form.querySelectorAll(".contact-form__input");
        inputs.forEach((input) => {
            if (input.type === "tel" || input.id === "contact-phone") {
                input.value = "";
            } else {
                input.value = "";
            }
            input.classList.remove("is-invalid");
        });

        this.setLoadingState(false);
    }

    showSuccessView() {
        if (!this.successView) {
            return;
        }

        if (this.formWrapper) {
            this.formWrapper.style.setProperty("display", "none", "important");
        }

        if (this.form) {
            this.form.style.setProperty("display", "none", "important");
        }

        this.successView.style.setProperty("display", "flex", "important");
    }

    hideSuccessView() {
        if (!this.successView) {
            return;
        }

        this.successView.style.setProperty("display", "none", "important");

        if (this.formWrapper) {
            this.formWrapper.style.setProperty("display", this.wrapperDisplayType, "important");
        }

        if (this.form) {
            this.form.style.setProperty("display", "block", "important");
        }
    }
}

const initContactForm = () => {
    document.querySelectorAll(".contacts .contacts__left-form").forEach((container) => {
        new ContactFormBlock(container, "contacts__success", "flex");
    });

    document.querySelectorAll(".cta .cta__form").forEach((container) => {
        new ContactFormBlock(container, "contact-form__success", "block");
    });
};

load(initContactForm);

export default initContactForm;


